import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  ArrowRight, ArrowLeft, Check, ChevronRight, Star,
  User, Heart, Camera, MessageCircle, Settings, Shield,
  Mail, Lock, Eye, EyeOff, Upload, X, Plus, Minus,
  Sparkles, Trophy, Target, Gift, Coffee, Music,
  Book, Plane, Dumbbell, Palette, Code, Mountain
} from 'lucide-react';
import { useNotifications } from './SmartNotificationComponents';

// Progressive Profile Completion
export const ProgressiveProfileSetup = ({ onComplete, initialData = {} }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [profileData, setProfileData] = useState({
    basicInfo: {
      name: '',
      age: '',
      gender: '',
      interested_in: [],
      location: '',
      latitude: null,
      longitude: null,
      enable_location_matching: false,
      preferred_distance_miles: 25,
      ...initialData.basicInfo
    },
    photos: [],
    interests: [],
    bio: '',
    preferences: {
      ageRange: [18, 35],
      maxDistance: 25,
      lookingFor: 'relationship'
    },
    ...initialData
  });
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const { addNotification, triggerCelebration } = useNotifications();

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to Shot Clock Dating! 🎉',
      subtitle: "Let's create your perfect profile in just a few steps",
      component: WelcomeStep,
      required: false
    },
    {
      id: 'basic-info',
      title: 'Tell us about yourself',
      subtitle: 'Basic information to get started',
      component: BasicInfoStep,
      required: true
    },
    {
      id: 'photos',
      title: 'Add your best photos',
      subtitle: 'Show your personality with great pictures',
      component: PhotosStep,
      required: true,
      minPhotos: 2
    },
    {
      id: 'interests',
      title: 'What do you love?',
      subtitle: 'Select your interests to find better matches',
      component: InterestsStep,
      required: true,
      minInterests: 3
    },
    {
      id: 'bio',
      title: 'Write your story',
      subtitle: 'Tell potential matches what makes you unique',
      component: BioStep,
      required: true
    },
    {
      id: 'preferences',
      title: 'Dating preferences',
      subtitle: 'Set your preferences for better matches',
      component: BasicInfoStep, // Using BasicInfoStep temporarily until PreferencesStep is implemented
      required: false
    },
    {
      id: 'complete',
      title: 'Profile complete! 🌟',
      subtitle: 'You\'re ready to start dating',
      component: CompletionStep,
      required: false
    }
  ];

  useEffect(() => {
    calculateCompletionPercentage();
  }, [profileData]);

  const calculateCompletionPercentage = () => {
    let completed = 0;
    let total = 0;

    // Basic info (25%)
    total += 25;
    if (profileData.basicInfo.name && profileData.basicInfo.age && profileData.basicInfo.location) {
      completed += 25;
    }

    // Photos (25%)
    total += 25;
    if (profileData.photos.length >= 2) {
      completed += 25;
    }

    // Interests (20%)
    total += 20;
    if (profileData.interests.length >= 3) {
      completed += 20;
    }

    // Bio (20%)
    total += 20;
    if (profileData.bio && profileData.bio.length >= 50) {
      completed += 20;
    }

    // Preferences (10%)
    total += 10;
    if (profileData.preferences.ageRange && profileData.preferences.maxDistance) {
      completed += 10;
    }

    setCompletionPercentage(Math.round((completed / total) * 100));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      
      // Trigger celebration for major milestones
      if (currentStep === 2) { // After photos
        triggerCelebration({
          type: 'achievement',
          title: 'Great photos! 📸',
          message: 'Your profile is looking amazing!',
          duration: 2000
        });
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateProfileData = (section, data) => {
    setProfileData(prev => ({
      ...prev,
      [section]: { ...prev[section], ...data }
    }));
  };

  const canProceedToNext = () => {
    const step = steps[currentStep];
    if (!step.required) return true;

    switch (step.id) {
      case 'basic-info':
        return profileData.basicInfo.name && profileData.basicInfo.age && profileData.basicInfo.location;
      case 'photos':
        return profileData.photos.length >= (step.minPhotos || 1);
      case 'interests':
        return profileData.interests.length >= (step.minInterests || 1);
      case 'bio':
        return profileData.bio && profileData.bio.length >= 20;
      default:
        return true;
    }
  };

  const handleComplete = () => {
    triggerCelebration({
      type: 'level_up',
      title: 'Profile Complete! 🎉',
      message: 'You\'re ready to start meeting amazing people!',
      reward: '3 free super likes',
      duration: 3000
    });
    
    addNotification({
      type: 'success',
      title: 'Welcome to Shot Clock Dating!',
      message: 'Your profile is live and ready for matches.',
      isToast: true,
      duration: 5000
    });

    onComplete(profileData);
  };

  const currentStepComponent = steps[currentStep]?.component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-x-hidden">
      {/* Progress Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {currentStep > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={prevStep}
                  className="p-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <div>
                <h1 className="text-lg font-semibold text-gray-800">
                  Setup Profile
                </h1>
                <p className="text-sm text-gray-500">
                  Step {currentStep + 1} of {steps.length}
                </p>
              </div>
            </div>
            
            {/* Removed the completion percentage box for better aesthetics */}
          </div>
          
          <Progress 
            value={(currentStep / (steps.length - 1)) * 100} 
            className="h-2"
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-md mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {currentStepComponent && React.createElement(currentStepComponent, {
              data: profileData,
              onUpdate: updateProfileData,
              onNext: nextStep,
              onComplete: handleComplete,
              canProceed: canProceedToNext(),
              stepData: steps[currentStep]
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Individual Step Components
const WelcomeStep = ({ onNext, stepData }) => {
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="text-center py-8"
    >
      <motion.div
        animate={{ rotate: [0, 10, -10, 0] }}
        transition={{ duration: 2, repeat: Infinity, delay: 1 }}
        className="text-8xl mb-6"
      >
        💕
      </motion.div>
      
      <h1 className="text-3xl font-bold text-gray-800 mb-4">
        {stepData.title}
      </h1>
      
      <p className="text-gray-600 text-lg mb-8 leading-relaxed">
        {stepData.subtitle}
      </p>
      
      <div className="space-y-4 mb-8">
        <div className="flex items-center space-x-3 text-left">
          <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
            <Heart className="w-4 h-4 text-rose-500" />
          </div>
          <span className="text-gray-700">AI-powered matching for better connections</span>
        </div>
        
        <div className="flex items-center space-x-3 text-left">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-blue-500" />
          </div>
          <span className="text-gray-700">3-5 minute speed dating sessions</span>
        </div>
        
        <div className="flex items-center space-x-3 text-left">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <Shield className="w-4 h-4 text-green-500" />
          </div>
          <span className="text-gray-700">Safe and verified community</span>
        </div>
      </div>
      
      <Button 
        onClick={onNext}
        size="lg" 
        className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
      >
        Let's Get Started 🚀
      </Button>
    </motion.div>
  );
};

const BasicInfoStep = ({ data, onUpdate, canProceed, onNext }) => {
  const [formData, setFormData] = useState(data.basicInfo || {});
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showLocationPrefs, setShowLocationPrefs] = useState(false);
  
  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onUpdate('basicInfo', updated);
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Reverse geocoding to get city/state
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          
          if (response.ok) {
            const locationData = await response.json();
            const location = `${locationData.city}, ${locationData.principalSubdivision}`;
            
            handleChange('location', location);
            handleChange('latitude', latitude);
            handleChange('longitude', longitude);
          }
        } catch (error) {
          // Fallback: just use coordinates
          handleChange('location', `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
          handleChange('latitude', latitude);
          handleChange('longitude', longitude);
        }
        
        setGettingLocation(false);
      },
      (error) => {
        alert('Unable to get your location. Please enter it manually.');
        setGettingLocation(false);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Tell us about yourself
        </h2>
        <p className="text-gray-600">
          Let's start with the basics
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your name
          </label>
          <Input
            type="text"
            placeholder="Enter your first name"
            value={formData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            className="text-lg p-3"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your age
          </label>
          <Input
            type="number"
            placeholder="25"
            min="18"
            max="100"
            value={formData.age || ''}
            onChange={(e) => handleChange('age', e.target.value)}
            className="text-lg p-3"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your gender
          </label>
          <select
            value={formData.gender || ''}
            onChange={(e) => handleChange('gender', e.target.value)}
            className="w-full p-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
          >
            <option value="">Select your gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="non-binary">Non-binary</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            I'm interested in dating
          </label>
          <div className="space-y-2">
            {['male', 'female', 'non-binary', 'other'].map((genderOption) => (
              <label key={genderOption} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={(formData.interested_in || []).includes(genderOption)}
                  onChange={(e) => {
                    const currentInterests = formData.interested_in || [];
                    if (e.target.checked) {
                      handleChange('interested_in', [...currentInterests, genderOption]);
                    } else {
                      handleChange('interested_in', currentInterests.filter(g => g !== genderOption));
                    }
                  }}
                  className="w-4 h-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
                />
                <span className="text-sm text-gray-700 capitalize">{genderOption}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            You can select multiple options
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>
          <div className="flex space-x-2">
            <Input
              type="text"
              placeholder="City, State or City, Country"
              value={formData.location || ''}
              onChange={(e) => handleChange('location', e.target.value)}
              className="text-lg p-3 flex-1"
            />
            <Button
              type="button"
              onClick={getCurrentLocation}
              disabled={gettingLocation}
              variant="outline"
              className="px-3 py-2"
            >
              {gettingLocation ? '⏳' : '📍'}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            We'll use this to help you find matches nearby (optional)
          </p>
        </div>

        {/* Location-based matching preference */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-900">Location-Based Matching</h4>
            <button
              type="button"
              onClick={() => handleChange('enable_location_matching', !formData.enable_location_matching)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.enable_location_matching ? 'bg-rose-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.enable_location_matching ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <p className="text-sm text-gray-600">
            Show me matches based on distance from my location
          </p>
          
          {formData.enable_location_matching && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum distance: {formData.preferred_distance_miles || 25} miles
              </label>
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={formData.preferred_distance_miles || 25}
                onChange={(e) => handleChange('preferred_distance_miles', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5 miles</span>
                <span>25 miles</span>
                <span>50 miles</span>
                <span>100+ miles</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="pt-6">
        <Button 
          onClick={onNext}
          disabled={!canProceed}
          size="lg" 
          className="w-full"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};



// Photos Step Component
const PhotosStep = ({ data, onUpdate, canProceed, onNext }) => {
  const [photos, setPhotos] = useState(data.photos || []);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  const MAX_PHOTOS = 6;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  console.log('📸 PhotosStep component mounted with photos:', photos.length);

  const validateFile = (file) => {
    const errors = [];
    if (!ALLOWED_TYPES.includes(file.type)) {
      errors.push('Please upload only JPG, PNG, or WebP images');
    }
    if (file.size > MAX_FILE_SIZE) {
      errors.push('File size must be less than 5MB');
    }
    return errors;
  };

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 1200;
          const maxHeight = 1200;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            }));
          }, 'image/jpeg', 0.85);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadPhoto = async (file) => {
    const photoId = Date.now() + Math.random();
    console.log('🎯 uploadPhoto called for:', file.name);

    try {
      // Validate file
      console.log('🔍 Validating file...');
      const validationErrors = validateFile(file);
      if (validationErrors.length > 0) {
        console.error('❌ Validation failed:', validationErrors);
        throw new Error(validationErrors[0]);
      }
      console.log('✅ File validated');

      // Compress image
      console.log('🗜️ Compressing image...');
      setUploadProgress(prev => ({ ...prev, [photoId]: 20 }));
      const compressedFile = await compressImage(file);
      console.log('✅ Image compressed:', compressedFile.size, 'bytes');

      // Create form data
      const formData = new FormData();
      formData.append('file', compressedFile, file.name);

      setUploadProgress(prev => ({ ...prev, [photoId]: 40 }));

      // Upload to server
      const token = localStorage.getItem('token');
      const uploadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/upload-photo`;
      console.log('📡 Uploading to:', uploadUrl);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      setUploadProgress(prev => ({ ...prev, [photoId]: 80 }));

      console.log('📨 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Upload failed. Status:', response.status, 'Error:', errorText);
        throw new Error('Upload failed');
      }

      const photoData = await response.json();
      console.log('✅ Upload successful:', photoData);

      setUploadProgress(prev => ({ ...prev, [photoId]: 100 }));

      // Add to photos array
      const newPhotos = [...photos, photoData];
      setPhotos(newPhotos);
      onUpdate('photos', newPhotos);

      // Clean up progress
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[photoId];
          return newProgress;
        });
      }, 1000);

    } catch (error) {
      console.error('❌ Photo upload error:', error);
      console.error('Error details:', error.message);
      alert(`Photo upload failed: ${error.message}`);
      // Remove progress for this photo
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[photoId];
          return newProgress;
        });
      }, 2000);
    }
  };

  const handleFileSelect = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    console.log('📁 File select triggered');
    const files = Array.from(event.target.files);
    console.log('📁 Files selected:', files.length);

    if (files.length === 0) {
      console.log('⚠️ No files selected');
      return;
    }

    if (photos.length + files.length > MAX_PHOTOS) {
      alert(`You can only upload up to ${MAX_PHOTOS} photos`);
      return;
    }

    setUploading(true);
    console.log('⏳ Starting upload process...');

    for (const file of files) {
      console.log('📤 Uploading file:', file.name, file.size, 'bytes');
      await uploadPhoto(file);
    }

    setUploading(false);
    console.log('✅ Upload process complete');

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const updatedPhotos = photos.filter(p => p.id !== photoId);
      setPhotos(updatedPhotos);
      onUpdate('photos', updatedPhotos);
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo');
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <Camera className="w-12 h-12 text-rose-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Add your best photos
        </h2>
        <p className="text-gray-600">
          Upload at least 2 photos to show your personality
        </p>
      </div>

      {/* Photo Grid */}
      <div className="grid grid-cols-3 gap-4">
        {/* Existing Photos */}
        {photos.map((photo, index) => (
          <div key={photo.id || index} className="relative aspect-square group">
            <img
              src={photo.photo_url || photo.url}
              alt={`Photo ${index + 1}`}
              className="w-full h-full object-cover rounded-2xl"
            />
            <button
              type="button"
              onClick={() => removePhoto(photo.id)}
              className="absolute top-2 right-2 bg-red-500/80 backdrop-blur-sm text-white p-2 rounded-full hover:bg-red-600/80 transition-colors opacity-0 group-hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
            {index === 0 && (
              <div className="absolute bottom-2 left-2 bg-rose-500 text-white text-xs px-2 py-1 rounded-full">
                Main Photo
              </div>
            )}
          </div>
        ))}

        {/* Upload Progress */}
        {Object.entries(uploadProgress).map(([id, progress]) => (
          <div key={id} className="aspect-square border-2 border-gray-200 rounded-2xl flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2 animate-pulse" />
              <div className="text-sm text-gray-600">{progress}%</div>
            </div>
          </div>
        ))}

        {/* Upload Slots */}
        {Array.from({ length: MAX_PHOTOS - photos.length - Object.keys(uploadProgress).length }).map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🖱️ Upload button clicked');
              console.log('📂 File input ref:', fileInputRef.current);
              fileInputRef.current?.click();
            }}
            disabled={uploading}
            className="aspect-square border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center hover:border-rose-400 hover:bg-rose-50 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Camera className="h-8 w-8 text-gray-400 group-hover:text-rose-500 mb-2" />
            <span className="text-xs text-gray-500 group-hover:text-rose-600">Add Photo</span>
          </button>
        ))}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Photo Guidelines */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm">
        <p className="font-medium text-blue-800 mb-1">Photo Guidelines:</p>
        <ul className="text-blue-700 space-y-1 text-xs">
          <li>• Clear, recent photos of yourself</li>
          <li>• At least 2 photos required</li>
          <li>• Maximum 6 photos</li>
          <li>• JPG, PNG, or WebP format</li>
          <li>• Maximum 5MB per photo</li>
        </ul>
      </div>

      {/* Continue Button */}
      {photos.length >= 2 && (
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onNext();
          }}
          className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
        >
          Continue <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      )}

      {photos.length < 2 && (
        <div className="text-center text-sm text-gray-500">
          Upload {2 - photos.length} more photo{2 - photos.length !== 1 ? 's' : ''} to continue
        </div>
      )}
    </div>
  );
};

const InterestsStep = ({ data, onUpdate, canProceed, onNext }) => {
  const [selectedInterests, setSelectedInterests] = useState(data.interests || []);

  const interestCategories = [
    {
      name: 'Lifestyle',
      interests: [
        { id: 'fitness', name: 'Fitness', icon: <Dumbbell className="w-4 h-4" /> },
        { id: 'travel', name: 'Travel', icon: <Plane className="w-4 h-4" /> },
        { id: 'food', name: 'Food & Cooking', icon: <Coffee className="w-4 h-4" /> },
        { id: 'music', name: 'Music', icon: <Music className="w-4 h-4" /> }
      ]
    },
    {
      name: 'Hobbies',
      interests: [
        { id: 'reading', name: 'Reading', icon: <Book className="w-4 h-4" /> },
        { id: 'art', name: 'Art & Design', icon: <Palette className="w-4 h-4" /> },
        { id: 'tech', name: 'Technology', icon: <Code className="w-4 h-4" /> },
        { id: 'outdoors', name: 'Outdoors', icon: <Mountain className="w-4 h-4" /> }
      ]
    }
  ];

  const toggleInterest = (interestId) => {
    const updated = selectedInterests.includes(interestId)
      ? selectedInterests.filter(id => id !== interestId)
      : [...selectedInterests, interestId];
    
    setSelectedInterests(updated);
    onUpdate('interests', updated);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          What do you love?
        </h2>
        <p className="text-gray-600">
          Select at least 3 interests
        </p>
      </div>

      <div className="space-y-6">
        {interestCategories.map(category => (
          <div key={category.name}>
            <h3 className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
              {category.name}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {category.interests.map(interest => (
                <motion.button
                  key={interest.id}
                  onClick={() => toggleInterest(interest.id)}
                  className={`flex items-center space-x-3 p-4 rounded-xl border-2 transition-all ${
                    selectedInterests.includes(interest.id)
                      ? 'border-rose-500 bg-rose-50 text-rose-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className={`${
                    selectedInterests.includes(interest.id) ? 'text-rose-500' : 'text-gray-500'
                  }`}>
                    {interest.icon}
                  </div>
                  <span className="font-medium text-sm">{interest.name}</span>
                </motion.button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="text-center">
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          {selectedInterests.length} selected
        </Badge>
      </div>

      <div className="pt-6">
        <Button 
          onClick={onNext}
          disabled={!canProceed}
          size="lg" 
          className="w-full"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

const BioStep = ({ data, onUpdate, canProceed, onNext }) => {
  const [bio, setBio] = useState(data.bio || '');
  const [charCount, setCharCount] = useState(0);
  
  const maxChars = 500;
  const minChars = 20;

  const handleBioChange = (value) => {
    if (value.length <= maxChars) {
      setBio(value);
      setCharCount(value.length);
      onUpdate('bio', value);
    }
  };

  const bioPrompts = [
    "What makes you laugh?",
    "Your perfect weekend looks like...",
    "One thing you can't live without:",
    "Your hidden talent:",
    "Best travel story:"
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Write your story
        </h2>
        <p className="text-gray-600">
          Tell potential matches what makes you unique
        </p>
      </div>

      <div>
        <Textarea
          placeholder="I love exploring new places, trying different cuisines, and having deep conversations over coffee. I'm passionate about photography and always looking for the perfect sunset shot..."
          value={bio}
          onChange={(e) => handleBioChange(e.target.value)}
          className="min-h-[120px] text-base p-4 resize-none"
          maxLength={maxChars}
        />
        
        <div className="flex justify-between items-center mt-2">
          <div className="text-sm text-gray-500">
            {charCount < minChars && (
              <span>At least {minChars - charCount} more characters needed</span>
            )}
          </div>
          <div className={`text-sm ${
            charCount >= minChars ? 'text-green-600' : 'text-gray-500'
          }`}>
            {charCount}/{maxChars}
          </div>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-3">
          Need inspiration? Try these prompts:
        </p>
        <div className="space-y-2">
          {bioPrompts.map((prompt, index) => (
            <button
              key={index}
              onClick={() => {
                const newBio = bio + (bio ? '\n\n' : '') + prompt + ' ';
                handleBioChange(newBio);
              }}
              className="text-left w-full p-3 text-sm bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              💭 {prompt}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-6">
        <Button 
          onClick={onNext}
          disabled={!canProceed}
          size="lg" 
          className="w-full"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

const CompletionStep = ({ data, onComplete }) => {
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="text-center py-8"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: [0, 1.2, 1] }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="text-8xl mb-6"
      >
        🎉
      </motion.div>
      
      <h1 className="text-3xl font-bold text-gray-800 mb-4">
        Profile Complete!
      </h1>
      
      <p className="text-gray-600 text-lg mb-8">
        You're ready to start meeting amazing people
      </p>
      
      <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl p-6 mb-8">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-rose-600">3</div>
            <div className="text-gray-600">Free Super Likes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">7</div>
            <div className="text-gray-600">Days Premium Trial</div>
          </div>
        </div>
      </div>
      
      <Button 
        onClick={onComplete}
        size="lg" 
        className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 mb-4"
      >
        Start Dating Now! 💕
      </Button>
      
      <p className="text-xs text-gray-500">
        You can always update your profile later in settings
      </p>
    </motion.div>
  );
};

// Interactive Tutorial Component
export const InteractiveTutorial = ({ onComplete, tutorialSteps }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const overlayRef = useRef(null);

  const defaultSteps = [
    {
      target: '[data-tutorial="navigation"]',
      title: 'Welcome to Shot Clock Dating! 👋',
      content: 'Let\'s take a quick tour to help you get started.',
      placement: 'bottom'
    },
    {
      target: '[data-tutorial="profile"]',
      title: 'Your Profile',
      content: 'Tap here to view and edit your profile anytime.',
      placement: 'top'
    },
    {
      target: '[data-tutorial="dating"]',
      title: 'Speed Dating',
      content: 'Start your 3-5 minute speed dating sessions here!',
      placement: 'top'
    },
    {
      target: '[data-tutorial="messages"]',
      title: 'Messages',
      content: 'Chat with your matches and build connections.',
      placement: 'top'
    }
  ];

  const steps = tutorialSteps || defaultSteps;
  const currentStepData = steps[currentStep];

  useEffect(() => {
    if (!isVisible) return;

    // Highlight target element
    const targetElement = document.querySelector(currentStepData.target);
    if (targetElement) {
      targetElement.style.position = 'relative';
      targetElement.style.zIndex = '1001';
      targetElement.style.boxShadow = '0 0 0 4px rgba(236, 72, 153, 0.5)';
      targetElement.style.borderRadius = '8px';
    }

    return () => {
      if (targetElement) {
        targetElement.style.position = '';
        targetElement.style.zIndex = '';
        targetElement.style.boxShadow = '';
        targetElement.style.borderRadius = '';
      }
    };
  }, [currentStep, currentStepData, isVisible]);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTutorial = () => {
    setIsVisible(false);
    onComplete();
  };

  if (!isVisible) return null;

  return (
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
    >
      {/* Tutorial Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="fixed bottom-4 left-4 right-4 max-w-sm mx-auto bg-white rounded-2xl shadow-2xl p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
            {currentStep + 1} of {steps.length}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={completeTutorial}
            className="p-1"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <h3 className="text-lg font-bold text-gray-800 mb-2">
          {currentStepData.title}
        </h3>
        
        <p className="text-gray-600 mb-6">
          {currentStepData.content}
        </p>

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            Previous
          </Button>
          
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              onClick={completeTutorial}
              className="text-gray-500"
            >
              Skip
            </Button>
            
            <Button onClick={nextStep}>
              {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Enhanced Email Verification
export const EnhancedEmailVerification = ({ email, onVerified, onResend }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);
  const { addNotification } = useNotifications();

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    // Countdown timer
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-verify when all digits are filled
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      verifyCode(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const verifyCode = async (codeString) => {
    try {
      setError('');
      // API call to verify code would go here
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API
      
      addNotification({
        type: 'success',
        title: 'Email verified! ✅',
        message: 'Welcome to Shot Clock Dating!',
        isToast: true,
        duration: 3000
      });
      
      onVerified();
    } catch (error) {
      setError('Invalid verification code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      await onResend();
      setTimeLeft(60);
      addNotification({
        type: 'info',
        title: 'Code sent!',
        message: 'Check your email for the new verification code.',
        isToast: true,
        duration: 3000
      });
    } catch (error) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center p-4 overflow-x-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-16 h-16 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <Mail className="w-8 h-8 text-white" />
            </motion.div>
            <CardTitle className="text-2xl font-bold text-gray-800">
              Verify your email
            </CardTitle>
            <p className="text-gray-600 mt-2">
              We sent a 6-digit code to<br />
              <strong>{email}</strong>
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Code Input */}
            <div>
              <div className="flex justify-center space-x-2 mb-4">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-rose-500 focus:ring-2 focus:ring-rose-200 transition-all"
                  />
                ))}
              </div>
              
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600 text-center"
                >
                  {error}
                </motion.p>
              )}
            </div>

            {/* Resend Timer */}
            <div className="text-center">
              {timeLeft > 0 ? (
                <p className="text-sm text-gray-500">
                  Resend code in <strong>{timeLeft}s</strong>
                </p>
              ) : (
                <Button
                  variant="ghost"
                  onClick={handleResend}
                  disabled={isResending}
                  className="text-rose-500 hover:text-rose-600"
                >
                  {isResending ? 'Sending...' : 'Resend code'}
                </Button>
              )}
            </div>

            <div className="text-center text-sm text-gray-500">
              Didn't receive the email? Check your spam folder or{' '}
              <button className="text-rose-500 hover:text-rose-600 font-medium">
                update your email address
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default {
  ProgressiveProfileSetup,
  InteractiveTutorial,
  EnhancedEmailVerification
};